import * as msal from "@azure/msal-browser";
import config from "../config";

export default class ModernAuthService {
    constructor() {
        this.applicationConfig = {
            backendScopes: {
                scopes: [`${config.clientId}/.default`]
            },
            graphApiScopes: {
                scopes: ["https://graph.microsoft.com/User.Read"]
            }
        };

        const msalConfig = {
            auth: {
                clientId: config.clientId,
                authority: config.authority,
                redirectUri: this.getRedirectUri(),
                postLogoutRedirectUri: this.getPostLogoutRedirectUri(),
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: "sessionStorage",
                storeAuthStateInCookie: false
            },
            system: {
                allowNativeBroker: false,
                loggerOptions: {
                    loggerCallback: (level, message) => {
                        // MSAL logging - suppressed for production
                    },
                    logLevel: process.env.NODE_ENV === "development" ? 3 : 1
                }
            }
        };

        this.msalInstance = new msal.PublicClientApplication(msalConfig);
        this.activeAccount = null;
        this.isInitialized = false;
        this.authCallbacks = new Set();
        this.lastAuthResponse = null;
    }

    async initialize() {
        if (this.isInitialized) return;

        try {
            await this.msalInstance.initialize();

            // Handle redirect result if coming back from authentication
            const response = await this.msalInstance.handleRedirectPromise();
            if (response) {
                this.setActiveAccount(response.account);
                this.notifyAuthCallbacks(true);

                // Don't redirect here - let the callback component handle it
                // Store the response for the callback component to use
                this.lastAuthResponse = response;
                this.isInitialized = true;
                return;
            }

            // Try to set active account from cache
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length > 0) {
                this.setActiveAccount(accounts[0]);
                await this.attemptSilentAuthentication();
            }

            this.isInitialized = true;
        } catch (error) {
            // Error initializing MSAL - suppressed for production
            this.isInitialized = true;
        }
    }

    async attemptSilentAuthentication() {
        if (!this.activeAccount) return false;

        try {
            const silentRequest = {
                scopes: this.applicationConfig.backendScopes.scopes,
                account: this.activeAccount,
                forceRefresh: false
            };

            await this.msalInstance.acquireTokenSilent(silentRequest);
            this.notifyAuthCallbacks(true);
            return true;
        } catch (error) {
            // Silent authentication failed, but user might still be logged in from cache
            if (error.name === "InteractionRequiredAuthError") {
                // Token expired or requires interaction
                return false;
            }
            // Silent authentication error - suppressed for production
            return false;
        }
    }

    async signIn(scopes = this.applicationConfig.backendScopes.scopes) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        const loginRequest = {
            scopes,
            prompt: "select_account"
        };

        try {
            await this.msalInstance.loginRedirect(loginRequest);
        } catch (error) {
            // Sign in error - suppressed for production
            throw error;
        }
    }

    async signOut() {
        if (!this.isInitialized) return;

        const logoutRequest = {
            account: this.activeAccount,
            postLogoutRedirectUri: window.location.origin
        };

        try {
            // Clear cached user data
            sessionStorage.removeItem("auth.user");
            await this.msalInstance.logoutRedirect(logoutRequest);
        } catch (error) {
            // Sign out error - suppressed for production
            // Fallback: clear cache and reload
            this.msalInstance.clearCache();
            this.activeAccount = null;
            sessionStorage.removeItem("auth.user");
            window.location.reload();
        }
    }

    async isAuthenticated() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        if (!this.activeAccount) return false;

        try {
            const silentRequest = {
                scopes: this.applicationConfig.backendScopes.scopes,
                account: this.activeAccount,
                forceRefresh: false
            };

            await this.msalInstance.acquireTokenSilent(silentRequest);
            return true;
        } catch (error) {
            return false;
        }
    }

    getLastAuthResponse() {
        return this.lastAuthResponse;
    }

    clearLastAuthResponse() {
        this.lastAuthResponse = null;
    }

    async getUser() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        if (!this.activeAccount) return null;

        try {
            const token = await this.getToken();
            if (!token) return null;

            const user = {
                username: this.activeAccount.name || this.activeAccount.username,
                email: this.activeAccount.username,
                roles: token.idTokenClaims?.roles || [],
                userId: this.activeAccount.homeAccountId,
                tenantId: this.activeAccount.tenantId
            };

            // Cache user in session storage for getRoles methods
            sessionStorage.setItem("auth.user", JSON.stringify(user));

            return user;
        } catch (error) {
            // Error getting user - suppressed for production
            return null;
        }
    }

    async getToken(scopes = this.applicationConfig.backendScopes.scopes) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        if (!this.activeAccount) {
            throw new Error("No active account. Please sign in first.");
        }

        try {
            const silentRequest = {
                scopes,
                account: this.activeAccount,
                forceRefresh: false
            };

            return await this.msalInstance.acquireTokenSilent(silentRequest);
        } catch (error) {
            if (error.name === "InteractionRequiredAuthError") {
                // Token expired, redirect to sign in
                const redirectPath = window.location.pathname + window.location.search;
                sessionStorage.setItem("auth.redirectPath", redirectPath);
                await this.signIn(scopes);
                return null;
            }
            throw error;
        }
    }

    getActiveAccount() {
        return this.activeAccount;
    }

    setActiveAccount(account) {
        this.activeAccount = account;
        this.msalInstance.setActiveAccount(account);
    }

    canAccessSite(siteName) {
        if (!this.activeAccount) return false;

        const user = this.getCachedUser();
        if (!user || !user.roles) return false;

        const role = user.roles.find(r =>
            r.includes(`service.${siteName}service`) ||
            r.includes("service.all")
        );

        return !!role;
    }

    hasServiceRole(serviceName, role) {
        if (!this.activeAccount) return false;

        const user = this.getCachedUser();
        if (!user || !user.roles) return false;

        const userRole = user.roles.find(r =>
            r.includes("service.all") ||
            r.includes(`service.${serviceName}service`)
        );

        return userRole ? userRole.includes(role) : false;
    }

    getTenants() {
        return this.getRoles("tenant.");
    }

    getOperators() {
        return this.getRoles("operator.");
    }

    getRoles(type) {
        const user = this.getCachedUser();
        if (!user || !user.roles) return [];

        const roles = user.roles.filter(r => r.startsWith(type));
        return roles.map(r => r.split(".").pop());
    }

    getCachedUser() {
        // Get user from session storage to avoid repeated API calls
        const cachedUser = sessionStorage.getItem("auth.user");
        return cachedUser ? JSON.parse(cachedUser) : null;
    }

    // Event system for authentication state changes
    onAuthStateChanged(callback) {
        this.authCallbacks.add(callback);

        // Return unsubscribe function
        return () => {
            this.authCallbacks.delete(callback);
        };
    }

    notifyAuthCallbacks(isAuthenticated) {
        this.authCallbacks.forEach(callback => {
            try {
                callback(isAuthenticated);
            } catch (error) {
                // Error in auth callback - suppressed for production
            }
        });
    }

    // Utility method to check if we're in the auth callback process
    isAuthCallback() {
        return window.location.pathname === "/auth/callback";
    }

    // Get the appropriate redirect URI for the current environment
    getRedirectUri() {
        // For local development, use current origin
        if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
            return window.location.origin + "/auth/callback";
        }

        // For deployed environments, use configured redirect URI
        const baseUri = process.env.VUE_APP_AUTHREDIRECT_URI || window.location.origin;
        return baseUri + "/auth/callback";
    }

    // Get the appropriate post-logout redirect URI
    getPostLogoutRedirectUri() {
        // For local development, use current origin
        if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
            return window.location.origin;
        }

        // For deployed environments, use configured redirect URI
        return process.env.VUE_APP_AUTHREDIRECT_URI || window.location.origin;
    }
}
